'use client';

import { IconChevronDown } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { PropsWithChildren, useCallback, useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDisclosure } from '@/hooks/use-disclosure';
import { cn } from '@/lib/cn';
type Option = {
  label: string;
  value: string;
};
interface Props {
  label: string;
  options: Option[];
  updateFilter: (value: string, checked: boolean) => void;
  appliedFilters: string[] | string | null;
}
interface ItemProps {
  label: string;
  isAnyChecked: boolean;
}
export const createOptions = (items?: {
  name: string;
  slug: string;
}[]) => items?.map(item => ({
  label: item.name,
  value: item.slug
})) || [];
export const FilterItem = ({
  label,
  options,
  updateFilter,
  appliedFilters
}: Props) => {
  const onCheckedChange = useCallback((value: string, checked: boolean) => {
    if (updateFilter) {
      updateFilter(value, checked);
      return;
    }
  }, [updateFilter]);
  const isChecked = useCallback((value: string) => {
    return appliedFilters?.includes(value) || false;
  }, [appliedFilters]);
  const isAnyChecked = useMemo(() => options.some(option => isChecked(option.value)), [options, isChecked]);
  return <div data-sentry-component="FilterItem" data-sentry-source-file="filter-item.tsx">
      <div className="hidden md:block">
        <DesktopFilterItem label={label} isAnyChecked={isAnyChecked} data-sentry-element="DesktopFilterItem" data-sentry-source-file="filter-item.tsx">
          <FiltersList options={options} isChecked={isChecked} onCheckedChange={onCheckedChange} data-sentry-element="FiltersList" data-sentry-source-file="filter-item.tsx" />
        </DesktopFilterItem>
      </div>

      <div className="md:hidden">
        <MobileFilterItem label={label} isAnyChecked={isAnyChecked} data-sentry-element="MobileFilterItem" data-sentry-source-file="filter-item.tsx">
          <FiltersList options={options} isChecked={isChecked} onCheckedChange={onCheckedChange} data-sentry-element="FiltersList" data-sentry-source-file="filter-item.tsx" />
        </MobileFilterItem>
      </div>
    </div>;
};
const MobileFilterItem = ({
  isAnyChecked,
  label,
  children
}: ItemProps & PropsWithChildren) => {
  const {
    isOpen,
    toggle
  } = useDisclosure();
  return <div data-sentry-component="MobileFilterItem" data-sentry-source-file="filter-item.tsx">
      <button className={cn('group w-full inline-flex justify-between items-center text-md font-medium', 'border rounded py-1.5 px-2.5 hover:bg-gray-50 hover:border-gray-200 whitespace-nowrap', {
      'text-primary border-primary border': isAnyChecked
    })} onClick={toggle}>
        <span>{label}</span>
        <div className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-500">
          <IconChevronDown className={`h-full w-full`} data-sentry-element="IconChevronDown" data-sentry-source-file="filter-item.tsx" />
        </div>
      </button>
      <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="filter-item.tsx">
        {isOpen && <motion.div initial={{
        height: 0
      }} animate={{
        height: 'auto'
      }} exit={{
        height: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} style={{
        overflow: 'hidden'
      }}>
            {children}
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const DesktopFilterItem = ({
  label,
  isAnyChecked,
  children
}: ItemProps & PropsWithChildren) => {
  return <Popover data-sentry-element="Popover" data-sentry-component="DesktopFilterItem" data-sentry-source-file="filter-item.tsx">
      <PopoverTrigger className={cn('group inline-flex justify-center text-md items-center font-medium', 'border rounded py-1 px-2 hover:bg-gray-50 hover:border-gray-200 whitespace-nowrap', {
      'text-primary border-primary border': isAnyChecked
    })} data-sentry-element="PopoverTrigger" data-sentry-source-file="filter-item.tsx">
        <span>{label}</span>
        <div className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-500">
          <IconChevronDown className={`h-full w-full`} data-sentry-element="IconChevronDown" data-sentry-source-file="filter-item.tsx" />
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="bg-background p-0 whitespace-nowrap" data-sentry-element="PopoverContent" data-sentry-source-file="filter-item.tsx">
        {children}
      </PopoverContent>
    </Popover>;
};
const FiltersList = ({
  options,
  isChecked,
  onCheckedChange
}: {
  options: Option[];
  isChecked: (value: string) => boolean;
  onCheckedChange: (value: string, checked: boolean) => void;
}) => {
  return <ul data-sentry-component="FiltersList" data-sentry-source-file="filter-item.tsx">
      {options.map(option => <li key={option.label} className="flex flex-col cursor-pointer hover:bg-gray-100 border-b border-color-gray-100 pr-3">
          <div className="flex items-center space-x-2">
            <label htmlFor={option.value} className="text-sm text-foreground cursor-pointer h-full w-full p-3 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 whitespace-nowrap">
              {option.label}
            </label>
            <Checkbox id={option.value} checked={isChecked(option.value)} onCheckedChange={checked => onCheckedChange(option.value, checked as boolean)} />
          </div>
        </li>)}
    </ul>;
};